import { FlyUserInvitation, UserRole } from '@fly/shared';
import { useFlyForm } from '@fly/shared-hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import { t } from 'i18next';
import { z } from 'zod';

const userInvitationSchema = z.object({
  email: z.string().refine(
    (value) => {
      const emails = value.split(',').map((email) => email.trim());
      return emails.every((email) => /\S+@\S+\.\S+/.test(email));
    },
    {
      message: t('app.common.fields.email.validation.invalid'),
    }
  ),
  role: z.nativeEnum(UserRole, {
    error: t('app.common.fields.validation.required'),
  }),
});

export type UserInvitationFormData = z.infer<typeof userInvitationSchema>;

export function formatData(data: UserInvitationFormData): FlyUserInvitation {
  return {
    email: data.email.includes(',')
      ? data.email.split(',').map((email) => email.trim())
      : [data.email.trim()],
    role: data.role,
  };
}

export interface UseUserInvitationFormReturn {
  form: ReturnType<typeof useFlyForm<UserInvitationFormData>>;
  handleCancel: () => void;
  resetForm: () => void;
  isLoading: boolean;
}

export const useUserInvitationForm = (
  isLoading: boolean = false
): UseUserInvitationFormReturn => {
  const form = useFlyForm<UserInvitationFormData>({
    resolver: zodResolver(userInvitationSchema),
    defaultValues: {
      email: '',
      role: UserRole.DEVELOPER,
    },
    mode: 'onChange',
  });

  const { reset } = form;

  const handleCancel = () => {
    reset();
  };

  const resetForm = () => {
    reset({
      email: '',
      role: UserRole.DEVELOPER,
    });
  };

  return {
    form,
    handleCancel,
    resetForm,
    isLoading,
  };
};
